import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'

import { ErrorMessage, FieldArray, Form, Formik, FormikProps, getIn } from 'formik'
import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import TextField from '../TextField'

import { dateToISO, isoDateStrToDateOrNull } from '../../helpers'
import theme from '../../theme'
import {
  getAttachmentsFromAttachmentListOrEmpty,
} from '../../models/attachmentListFnc'
import {
  AttachmentListRespType,
  AttachmentType,
  CaseMERespType,
  CaseReportId,
  CostRespType,
} from '../../models'
import { useStyles } from '../../pages/styled'
import { isEqual } from 'lodash/fp'
import BanButton from '../BanButton'
import DeleteIcon from '@material-ui/icons/Delete'
import LabelMessage from '../LabelMessage'
import ButtonSimply from '../ButtonSimply'
import { amountFilter } from '../../helpers'
import CircleLoadLayout from '../CircleLoadLayout'
import { Attachments, AttachmentsChangedEvent } from '../Attachments'
import { TreatmentExpensesPicker } from '../TreatmentExpensesPicker'
import { CurrencyPicker } from '../CurrencyPicker'
import * as yup from 'yup'
import { DatePicker } from '../DatePicker'
import ErrorSubmitBack from '../ErrorSubmitBack'
import { Ga4Form } from './Ga4Form'
import { BooleanField } from '../BooleanField'
import { amountReplace, emptyStrToUndefined, stringToNumber } from '../../helpers/miscFunctions'

const TreatmentExpensesForm = (params: {
  caseReportId: CaseReportId
  caseME: CaseMERespType | undefined
  onPageContinue: (costs: CostRespType[]) => void
  onPageBack: () => void
  isContinueInProgress: boolean
  isDataLoading?: boolean
  errorMessage: string
  setIfEqual: (isChanged: boolean) => void
  treatmentExpensesType: string
}) => {
  const classes = useStyles(theme)
  const { t } = useTranslation()

  const initialTreatmentExpensesCost = {
    expenseType: null,
    costDate: null,
    amount: '',
    currency: 'PLN',
    paidDirectly: undefined,
    attachments: [],
  }

  const costs =
    params.treatmentExpensesType === 'OP'
      ? params.caseME?.treatmentExpenses?.OP?.costs
      : params.treatmentExpensesType === 'IP'
        ? params.caseME?.treatmentExpenses?.IP?.costs
        : params.treatmentExpensesType === 'OTH'
          ? params.caseME?.treatmentExpenses?.OTH?.costs
          : null

  const partType = 'COST' + params.treatmentExpensesType

  const showTitle =
    params.treatmentExpensesType === 'OP'
      ? t('forthPg.titleOP')
      : params.treatmentExpensesType === 'IP'
        ? t('forthPg.titleIP')
        : params.treatmentExpensesType === 'OTH'
          ? t('forthPg.titleOTH')
          : null

  const transformFormikCostsToOutput = (costsFormik: any[]): CostRespType[] => {
    return (
      costsFormik?.map((cost: any, index) => {
        const editedAttachmentList: AttachmentListRespType[] = [
          {
            attachmentType: AttachmentType.INVOICE,
            attachments: cost.attachments,
          },
        ]
        return {
          orderNum: index + 1,
          expenseType: cost.expenseType,
          costDate: dateToISO(cost.costDate),
          amount: stringToNumber(cost.amount),
          currency: emptyStrToUndefined(cost.currency),
          paidDirectly: cost.paidDirectly,
          attachmentList: editedAttachmentList,
        }
      }) ?? []
    )
  }

  const transformInputItemsToFormikValues = (
    costs: CostRespType[] | null
  ): any[] => {
    return (
      costs?.map((cost: CostRespType) => {
        return {
          orderNum: cost.orderNum,
          expenseType: cost.expenseType ?? '',
          costDate: isoDateStrToDateOrNull(cost.costDate),
          amount: (cost?.amount ?? '').toString(),
          currency: cost?.currency ?? 'PLN',
          paidDirectly: cost?.paidDirectly,
          attachments: getAttachmentsFromAttachmentListOrEmpty(
            cost.attachmentList,
            AttachmentType.INVOICE
          ),
        }
      }) ?? []
    )
  }

  const formikInitialValues = {
    costs: !!costs?.length
      ? transformInputItemsToFormikValues(costs)
      : [initialTreatmentExpensesCost],
  }

  const handleFormSubmit = (values: any) => {
    const costsData: CostRespType[] = transformFormikCostsToOutput(values.costs)
    params.onPageContinue(costsData)
  }

  const costsLength = costs?.length

  const TreatmentExpensesSchema = () =>
    yup.object().shape({
      costs: yup.array().of(
        yup.object().shape({
          amount: yup
            .string()
            .required(t('forthPg.amount.required'))
            .test(
              'numberFormat',
              t('message.err'),
              (val: string | undefined) => {
                const _number = Number(val)
                return !isNaN(_number)
              }
            )
            .test(
              'moreThan0',
              t('amount.moreThan0'),
              (val: string | undefined) => {
                const _number = Number(val)
                return _number > 0
              }
            ),
          currency: yup
            .string()
            .nullable()
            .required(t('currency.required')),
          expenseType: yup
            .string()
            .required(t('forthPg.expenseType.required'))
            .nullable(),
          costDate: yup
            .date()
            .nullable()
            .required(t('dateMessage.required'))
            .max(new Date(), t('dateMessage.err2'))
            .min(new Date('1999-12-31'), t('dateMessage.err2'))
            .typeError(t('dateMessage.err1')),
          paidDirectly: yup.string().nullable().required(t('optionRequired')),
          attachments: yup.mixed().when('paidDirectly', {
            is: (paidDirectly: any) => paidDirectly === 'true',
            then: yup.array().nullable().min(1, t('uploadFile.err1')),
          }),
        })
      ),
    })

  const newOrderNum = useMemo(() =>
    costs && !!costsLength ? costs[costsLength - 1]?.orderNum + 1 : 1
    , [costs, costsLength])

  const formikRef: React.Ref<FormikProps<any>> = useRef(null)

  return params.isDataLoading ? (
    <CircleLoadLayout />
  ) : (
    <Ga4Form formName={'TreatmentExpensesForm'} >
      <Formik
        enableReinitialize
        initialValues={formikInitialValues}
        validationSchema={TreatmentExpensesSchema}
        onSubmit={handleFormSubmit}
        innerRef={formikRef}
      >
        {({
          initialValues,
          values,
          errors,
          handleChange,
          touched,
          handleBlur,
          setFieldValue,
        }) => {
          const isChanged = isEqual(values, initialValues)
          params.setIfEqual(isChanged)
          // console.log(values)
          return (
            <Form className={classes.root}>
              <Grid
                item
                sm={12}
                xl={12}
                xs={12}
                container
                direction="row"
                className={classes.gridMainForm}
              >
                <Grid
                  item
                  sm={9}
                  xl={9}
                  xs={12}
                  container
                  direction="row"
                  className={classes.gridWorkArea}
                >
                  <Grid
                    item
                    sm={12}
                    xs={12}
                    xl={12}
                    container
                    className={classes.gridResize}
                  >
                    <Typography className={classes.text} variant="h3">
                      {showTitle}
                    </Typography>
                    <FieldArray
                      name="costs"
                      render={({ push, remove }) => {
                        return (
                          <React.Fragment>
                            {values.costs &&
                              Boolean(values.costs?.length) &&
                              values.costs?.map(
                                (cost: CostRespType, index: number) => {
                                  return (
                                    <React.Fragment key={index}>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        {index > 0 && (
                                          <Grid
                                            item
                                            xl={12}
                                            xs={12}
                                            sm={12}
                                            className={classes.rowAlign}
                                          >
                                            <Typography
                                              className={classes.text}
                                              variant="h3"
                                            >
                                              {showTitle +
                                                '(' +
                                                (index + 1) +
                                                ')'}
                                            </Typography>
                                            {index ===
                                              (values.costs &&
                                                values?.costs?.length - 1) && (
                                                <BanButton
                                                  onClick={() => {
                                                    remove(index)
                                                  }}
                                                >
                                                  <DeleteIcon />
                                                </BanButton>
                                              )}
                                          </Grid>
                                        )}
                                        <Grid
                                          item
                                          xl={12}
                                          xs={12}
                                          sm={12}
                                          className={classes.fieldSize}
                                        >
                                          <TreatmentExpensesPicker
                                            name={`costs[${index}].expenseType`}
                                            expensesType={
                                              params.treatmentExpensesType
                                            }
                                            label={t('forthPg.expenseType.label')}
                                            selectedItemCode={
                                              values.costs[index]?.expenseType
                                            }
                                            onValueChanged={(code: string | null) => {
                                              setFieldValue(
                                                `costs[${index}].expenseType`,
                                                code
                                              )
                                            }}
                                            required
                                            onBlur={handleBlur}
                                            error={Boolean(
                                              getIn(
                                                errors,
                                                `costs[${index}].expenseType`
                                              ) &&
                                              getIn(
                                                touched,
                                                `costs[${index}].expenseType`
                                              )
                                            )}
                                            helperText={
                                              getIn(
                                                errors,
                                                `costs[${index}].expenseType`
                                              ) &&
                                                getIn(
                                                  touched,
                                                  `costs[${index}].expenseType`
                                                )
                                                ? getIn(
                                                  errors,
                                                  `costs[${index}].expenseType`
                                                )
                                                : null
                                            }
                                          />
                                        </Grid>
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Grid
                                          item
                                          xl={12}
                                          xs={12}
                                          sm={12}
                                          className={classes.fieldSize}
                                        >
                                          <DatePicker
                                            label={t('forthPg.costDate.label')}
                                            name={`costs[${index}].costDate`}
                                            helperText={
                                              getIn(
                                                errors,
                                                `costs[${index}].costDate`
                                              ) &&
                                                getIn(
                                                  touched,
                                                  `costs[${index}].costDate`
                                                )
                                                ? getIn(
                                                  errors,
                                                  `costs[${index}].costDate`
                                                )
                                                : null
                                            }
                                            error={Boolean(
                                              getIn(
                                                errors,
                                                `costs[${index}].costDate`
                                              ) &&
                                              getIn(
                                                touched,
                                                `costs[${index}].costDate`
                                              )
                                            )}
                                            required={true}
                                            minDate={new Date(2000, 0, 1)}
                                            maxDate={new Date()}
                                            value={
                                              values.costs &&
                                              values.costs[index]?.costDate
                                            }
                                            onChange={(value) => {
                                              setFieldValue(
                                                `costs[${index}].costDate`,
                                                value
                                              )
                                            }}
                                          />
                                        </Grid>
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Grid
                                          item
                                          xl={6}
                                          xs={6}
                                          sm={6}
                                          className={classes.fieldSize}
                                        >
                                          <LabelMessage
                                            name={`costs[${index}].amount`}
                                            touched={touched}
                                            errors={errors}
                                            text={t('forthPg.amount.label')}
                                          />
                                          <TextField
                                            regex={amountFilter}
                                            // @ts-ignore
                                            name={`costs[${index}].amount`}
                                            value={
                                              values.costs &&
                                              values.costs[index]?.amount
                                            }
                                            placeholder={t('forthPg.amount.hint')}
                                            onChange={(e) => {
                                              setFieldValue(
                                                `costs[${index}].amount`,
                                                amountReplace(e.target.value) ??
                                                ''
                                              )
                                            }}
                                            className={classes.fieldSize}
                                            InputProps={{ required: false }}
                                            inputProps={{ maxLength: 12 }}
                                            onBlur={handleBlur}
                                            required
                                          />
                                          <ErrorMessage
                                            name={`costs[${index}].amount`}
                                            component="div"
                                            className={classes.checkError}
                                          />
                                        </Grid>
                                        <Grid
                                          item
                                          xl={6}
                                          xs={6}
                                          sm={6}
                                          className={classes.fieldSize}
                                        >
                                          <CurrencyPicker
                                            name={`costs[${index}].currency`}
                                            label={t('currency.label')}
                                            selectedItemCode={
                                              values.costs[index].currency ?? null
                                            }
                                            onValueChanged={(
                                              selectedCurrencyCode: string | null
                                            ) => {
                                              setFieldValue(
                                                `costs[${index}].currency`,
                                                selectedCurrencyCode
                                              )
                                            }}
                                            error={
                                              getIn(
                                                errors,
                                                `costs[${index}].currency`
                                              ) &&
                                              getIn(
                                                touched,
                                                `costs[${index}].currency`
                                              )
                                            }
                                            helperText={
                                              getIn(
                                                errors,
                                                `costs[${index}].currency`
                                              ) &&
                                                getIn(
                                                  touched,
                                                  `costs[${index}].currency`
                                                )
                                                ? getIn(
                                                  errors,
                                                  `costs[${index}].currency`
                                                )
                                                : null
                                            }
                                            required={true}
                                            onBlur={handleBlur}
                                          />
                                        </Grid>
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        style={{ marginTop: '0.4rem' }}
                                      >
                                        <BooleanField
                                          aria-label={`costs[${index}].paidDirectly`}
                                          name={`costs[${index}].paidDirectly`}
                                          label={t(
                                            'forthPg.paidDirectly.label'
                                          )}
                                          value={values.costs[index]?.paidDirectly}
                                          onValueChange={(value) => setFieldValue(`costs[${index}].paidDirectly`, value)}
                                          required
                                          yesText={t('forthPg.paidDirectly.yes')}
                                          noText={t('forthPg.paidDirectly.no')}
                                          error={Boolean(getIn(errors, `costs[${index}].paidDirectly`) && getIn(touched, `costs[${index}].paidDirectly`))}
                                          helperText={Boolean(getIn(errors, `costs[${index}].paidDirectly`) && getIn(touched, `costs[${index}].paidDirectly`))
                                            ? getIn(errors, `costs[${index}].paidDirectly`)
                                            : null}
                                        />
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Attachments
                                          name={`costs[${index}].attachments`}
                                          caseReportId={params.caseReportId}
                                          labelText={t(`uploadAttachments.COST.INVOICE`)}
                                          attachmentType={
                                            AttachmentType.INVOICE
                                          }
                                          itemOrderNum={index + 1}
                                          //@ts-ignore
                                          part={partType}
                                          attachments={
                                            values.costs[index].attachments
                                          }
                                          onAttachmentsChanged={(
                                            event: AttachmentsChangedEvent
                                          ) => {
                                            setFieldValue(
                                              `costs[${index}].attachments`,
                                              event.value.attachments
                                            )
                                          }}
                                          onBlur={handleBlur}
                                          required={
                                            values.costs[index]?.paidDirectly ===
                                              'true'
                                              ? true
                                              : false
                                          }
                                          error={
                                            getIn(
                                              errors,
                                              `costs[${index}].attachments`
                                            ) &&
                                            getIn(
                                              touched,
                                              `costs[${index}].attachments`
                                            )
                                          }
                                          helperText={
                                            getIn(
                                              errors,
                                              `costs[${index}].attachments`
                                            ) &&
                                              getIn(
                                                touched,
                                                `costs[${index}].attachments`
                                              )
                                              ? getIn(
                                                errors,
                                                `costs[${index}].attachments`
                                              )
                                              : null
                                          }
                                        />
                                      </Grid>
                                    </React.Fragment>
                                  )
                                }
                              )}
                            <Grid
                              item
                              xl={12}
                              xs={12}
                              sm={12}
                              className={classes.row}
                            >
                              <Grid
                                item
                                xl={6}
                                xs={6}
                                sm={6}
                                className={classes.fieldSize}
                              >
                                <ButtonSimply
                                  onClick={() => {
                                    const initialValue = {
                                      ...initialTreatmentExpensesCost,
                                      attachments: [],
                                      orderNum: newOrderNum,
                                    }
                                    push(initialValue)
                                  }}
                                >
                                  {t('forthPg.addExpense')}
                                </ButtonSimply>
                              </Grid>
                            </Grid>
                          </React.Fragment>
                        )
                      }}
                    />
                    <ErrorSubmitBack
                      name={'InsuranceForm'}
                      formik={formikRef.current}
                      pending={
                        params.isContinueInProgress
                      }
                      backLinkText={t('forthPg.backBtn')}
                      onBackLinkClick={params.onPageBack}
                    >
                      {t('continueBtn')}
                    </ErrorSubmitBack>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Ga4Form >
  )
}

export default TreatmentExpensesForm
